import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Bot, X } from 'lucide-react';

import { useLivelloIndietro } from '@/hooks/useLivelloIndietro';
import { usePartitaControBot, type ErrorePartitaBot } from '@/hooks/usePartitaControBot';
import {
  livelliOfferti,
  NOTA_SPERIMENTALE_TESTID,
  SCELTE_INIZIALI,
  type LivelloBot,
  type LunghezzaMatch,
  type SceltePartitaBot,
} from '@/lib/qgammon/partitaBot';
import { cn } from '@/lib/cn';

interface OpzioneProps {
  scelta: boolean;
  /** Tre bottoni nella riga (livello sperimentale offerto): meno padding, perche' «Sperimentale» sta a 360 px. Con due, la forma di sempre. */
  stretto?: boolean;
  testId: string;
  onClick: () => void;
  children: string;
}

/** Un bottone di un gruppo a scelta singola (tavola `Foglio-bot.dc.html`). */
function Opzione({ scelta, stretto = false, testId, onClick, children }: OpzioneProps) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={scelta}
      data-testid={testId}
      onClick={onClick}
      className={cn(
        'min-h-11 min-w-0 flex-1 rounded-xl border-[1.5px] text-[13.5px] font-bold transition-colors',
        stretto ? 'px-1.5 leading-tight' : 'px-2',
        scelta ? 'border-brand bg-brand/15 text-brand' : 'border-border bg-bg text-fg-muted',
      )}
    >
      {children}
    </button>
  );
}

function Gruppo({ etichetta, children }: { etichetta: string; children: ReactNode }) {
  const id = useId();
  return (
    <>
      <p id={id} className="mb-2 mt-3.5 text-xs font-semibold text-fg-muted">
        {etichetta}
      </p>
      <div role="radiogroup" aria-labelledby={id} className="flex gap-2">
        {children}
      </div>
    </>
  );
}

/** La chiave del testo di ciascun livello (le altre due esistevano gia'). */
const ETICHETTA_LIVELLO: Record<LivelloBot, string> = {
  facile: 'ranked.bot.levelEasy',
  forte: 'ranked.bot.levelStrong',
  sperimentale: 'ranked.bot.levelExperimental',
};

function testoErrore(
  e: ErrorePartitaBot,
  t: (k: string, o?: Record<string, unknown>) => string,
): string {
  if (e.kind === 'sperimentaleNonDisponibile') return t('ranked.bot.experimentalUnavailable');
  if (e.kind === 'atMatchCap') {
    return e.max != null ? t('home.atMatchCapN', { max: e.max }) : t('home.atMatchCap');
  }
  if (e.kind === 'livelloAssente') return t('ranked.bot.levelUnavailable');
  return t('home.startError');
}

interface BotSheetProps {
  onClose: () => void;
}

/**
 * Il foglio «Gioca contro il bot» della scheda «Ranked» (reference «Anello che respira», L-2606,
 * 30/09, tavola `Foglio-bot.dc.html`): livello Facile/Forte, lunghezza 1/3/5 punti, orologio con o
 * senza (predefinito senza), e il bottone. La riga sotto il titolo lo dice prima di tutto:
 * «Allenamento: non conta per la classifica».
 *
 * Stesse convenzioni del foglio di conferma della classificata: fondo scurito che chiude, foglio dal
 * basso, un livello per il tasto indietro (`useLivelloIndietro`). Il corpo sul filo lo decide
 * `corpoPartitaBot` (`lib/qgammon/partitaBot.ts`), la chiamata `usePartitaControBot`.
 *
 * 🔑 Il bottone e' PIENO, e non e' estetica: la pratica non scrive ELO, e la sua forma resta quella
 * «invitante» — l'opposto dell'anello a contorno della classificata (`L-782`,
 * `tools/verify-classificata-forma.ts`).
 */
export function BotSheet({ onClose }: BotSheetProps) {
  const { t } = useTranslation('backgammon');
  const titoloId = useId();
  useLivelloIndietro(true, onClose);
  const { avvio, errore, gioca } = usePartitaControBot();
  const [scelte, setScelte] = useState<SceltePartitaBot>(SCELTE_INIZIALI);
  const livelli = livelliOfferti();
  const imposta = (parziale: Partial<SceltePartitaBot>) =>
    setScelte((s) => ({ ...s, ...parziale }));

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titoloId}
      data-testid="bot-sheet"
    >
      <button
        type="button"
        aria-label={t('ranked.close')}
        onClick={onClose}
        className="absolute inset-0 bg-black/60"
      />
      <div className="relative w-full max-w-md rounded-t-3xl bg-bg-secondary px-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-2.5">
        <div aria-hidden className="mx-auto mb-3 h-1 w-10 rounded-full bg-fg-muted/40" />
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 items-center gap-3">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-brand/15 text-brand">
              <Bot aria-hidden className="h-6 w-6" />
            </span>
            <div className="min-w-0">
              <h2
                id={titoloId}
                className="font-display text-[17px] font-extrabold tracking-tight text-fg"
              >
                {t('ranked.bot.title')}
              </h2>
              <p className="mt-0.5 text-[12.5px] text-fg-muted">{t('ranked.bot.note')}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('ranked.close')}
            className="-mr-2 -mt-2 grid h-11 w-11 shrink-0 place-items-center text-fg-muted active:opacity-80"
          >
            <X aria-hidden className="h-5 w-5" />
          </button>
        </div>

        <Gruppo etichetta={t('ranked.bot.level')}>
          {/* I bottoni escono dalle CHIAVI della mappa (`BOT_DEL_LIVELLO`): a flag spento sono due. */}
          {livelli.map((l) => (
            <Opzione
              key={l}
              stretto={livelli.length > 2}
              scelta={scelte.livello === l}
              testId={`bot-livello-${l}`}
              onClick={() => imposta({ livello: l })}
            >
              {t(ETICHETTA_LIVELLO[l])}
            </Opzione>
          ))}
        </Gruppo>
        {NOTA_SPERIMENTALE_TESTID !== null && (
          <p data-testid={NOTA_SPERIMENTALE_TESTID} className="mt-2 text-[12.5px] text-fg-muted">
            {t('ranked.bot.levelExperimentalNote')}
          </p>
        )}

        <Gruppo etichetta={t('home.matchLength')}>
          {([1, 3, 5] as const satisfies readonly LunghezzaMatch[]).map((n) => (
            <Opzione
              key={n}
              scelta={scelte.matchLength === n}
              testId={`bot-lunghezza-${n}`}
              onClick={() => imposta({ matchLength: n })}
            >
              {t(`home.pt${n}`)}
            </Opzione>
          ))}
        </Gruppo>

        <Gruppo etichetta={t('ranked.bot.clock')}>
          {([true, false] as const).map((conOrologio) => (
            <Opzione
              key={String(conOrologio)}
              scelta={scelte.orologio === conOrologio}
              testId={`bot-orologio-${conOrologio ? 'con' : 'senza'}`}
              onClick={() => imposta({ orologio: conOrologio })}
            >
              {t(conOrologio ? 'ranked.bot.clockOn' : 'ranked.bot.clockOff')}
            </Opzione>
          ))}
        </Gruppo>

        {errore && (
          <p role="alert" className="mt-3 text-sm text-danger">
            {testoErrore(errore, t)}
          </p>
        )}

        <button
          type="button"
          data-testid="bot-gioca"
          onClick={() => gioca(scelte)}
          disabled={avvio}
          className="mt-5 min-h-[50px] w-full rounded-xl bg-brand text-[15px] font-extrabold text-brand-fg transition-opacity active:opacity-80"
        >
          {avvio ? t('home.starting') : t('ranked.bot.play')}
        </button>
      </div>
    </div>
  );
}
